// src/services/topicPictureService.js
//
// TOPIC PICTURES — the cartoon on each topic card that students and
// educators see after picking a subject (DynamicPortalPage → .portal-topic-thumb).
//
// The card already shows topic.imageUrl, taken from the Topic Library when
// the page itself doesn't carry one. So "a picture on every card" is just
// "fill topicLibrary/{id}.imageUrl for every topic" — one picture per topic,
// and every state using that topic shows it.
//
// HOW A PICTURE GETS THERE
// ------------------------
//   1. makeTopicPictureDraft() asks the Image studio's generateImage Cloud
//      Function (admins only, Gemini) for one square cartoon. The result is
//      parked on the topic as `imageDraft` — NOT `imageUrl` — so nothing a
//      student sees changes yet.
//   2. approveTopicPicture() shrinks the picture to a 512×512 WebP (a card
//      shows it at ~72px; the original PNG is often 1–2 MB, and a page shows
//      a dozen cards), saves it to topic-images/cards/, sets imageUrl, and
//      clears the draft. If the browser can't shrink it (bucket CORS), the
//      original file is used instead — the picture still goes on.
//   3. discardTopicPicture() just clears the draft.
//
// Every write here is a MERGE of the picture fields only, so a topic's song,
// video, lyrics and status are never touched.
//
// PAGES WITH THEIR OWN PICTURE
// ----------------------------
// A page (portalLayouts doc) that carries its own imageUrl for a topic keeps
// it — the layout wins over the library. findPagePictures() lists those;
// applyLibraryPictures() blanks them (backing each page up to layoutBackups first,
// the same convention as Suggested topics) so the library picture shows.

import { doc, getDoc, getDocs, collection, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { db, functions, storage } from "../firebase/firebaseConfig";
import { fileKeyOf, legacyKey, exactKey } from "../utils/topicKey";

const SUBJECT_WORDS = {
  math: "math",
  science: "science",
  socialstudies: "social studies",
  other: "",
};

/** The topic's first few sung lines, without (Chorus) headers or credits. */
const firstLyrics = (lyrics, n = 4) =>
  (Array.isArray(lyrics) ? lyrics : String(lyrics || "").split("\n"))
    .map((l) => String(l || "").trim())
    .filter((l) => l && !/^[([].{1,40}[)\]]:?$/.test(l) && !/^©|smart board learning/i.test(l))
    .slice(0, n);

/** Card looks offered in the panel (sent as the Image studio's `style`). */
export const CARD_LOOKS = [
  { key: "sbl", label: "SBL cartoon" },
  { key: "storybook", label: "Storybook" },
];

export const CARD_QUALITIES = [
  { key: "standard", label: "Standard" },
  { key: "fast", label: "Fast (cheapest)" },
  { key: "best", label: "Best detail (costs most)" },
];

/**
 * The words sent to the picture maker for one topic. `idea` (typed in the
 * panel) replaces the automatic description of WHAT to draw; the framing
 * rules for a small card picture always apply.
 */
export const topicPicturePrompt = (topic, idea = "") => {
  const title = String(topic.title || topic.slug || "").trim();
  const subject = SUBJECT_WORDS[topic.subject] || "";
  const what = String(idea || "").trim();
  const lines = firstLyrics(topic.lyrics);
  return [
    what
      ? `A picture for the school ${subject} topic "${title}": ${what}`
      : `A picture that instantly says what the school ${subject} topic "${title}" is about.`,
    !what && topic.description ? `About the topic: ${String(topic.description).slice(0, 400)}` : "",
    !what && lines.length ? `It comes from a children's song with these lines: "${lines.join(" / ")}"` : "",
    "It is the small picture on a lesson card that K-8 students tap, so: ONE clear main subject, large and centred, " +
      "filling most of the square, on a simple soft background. It must still read when shown tiny (about 70 pixels " +
      "wide) and when cropped to a wide strip, so keep the subject in the middle with nothing important at the edges.",
  ]
    .filter(Boolean)
    .join("\n\n")
    .slice(0, 1990);
};

const libDoc = (topicId) => doc(db, "topicLibrary", String(topicId || "").trim());

/** Tell the Topic Library panel (if open) that a topic changed under it. */
const announce = (topicId, patch) => {
  try {
    window.dispatchEvent(new CustomEvent("sbl:topic-updated", { detail: { id: topicId, patch } }));
  } catch {
    /* no window (tests) */
  }
};

/**
 * Makes one picture and parks it on the topic as imageDraft.
 * Resolves the draft { url, path, prompt, model, at }.
 */
export const makeTopicPictureDraft = async (topic, { idea = "", look = "sbl", quality = "standard" } = {}) => {
  const id = topic.id || topic.slug;
  const call = httpsCallable(functions, "generateImage", { timeout: 170000 });
  const prompt = topicPicturePrompt(topic, idea);
  let res;
  try {
    res = await call({
      prompt,
      style: look,
      model: quality,
      aspectRatio: "1:1",
      name: `card-${fileKeyOf(legacyKey(id) || exactKey(id))}`,
      refs: [],
    });
  } catch (err) {
    const code = String(err?.code || "");
    if (code.includes("permission-denied")) throw new Error("Only SBL admin accounts can make pictures.");
    if (code.includes("deadline-exceeded")) throw new Error("The picture took too long. Try again.");
    if (code.includes("resource-exhausted")) throw new Error("Google's picture limit was reached. Wait a minute, then carry on.");
    if (code.includes("not-found") && /generateImage|not found/i.test(err?.message || "")) {
      throw new Error("The picture maker (generateImage) isn't deployed. See the Image studio setup.");
    }
    throw new Error(err?.message || String(err));
  }
  const { url, path } = res?.data || {};
  if (!url) throw new Error("No picture came back. Try a different idea.");
  const draft = { url, path: path || "", prompt, look, model: quality, at: new Date().toISOString() };
  const patch = { imageDraft: draft, ...(idea.trim() ? { imageIdea: idea.trim() } : {}) };
  await setDoc(libDoc(id), { ...patch, updatedAt: serverTimestamp() }, { merge: true });
  announce(id, patch);
  return draft;
};

/** Square 512px WebP (or JPEG) of a picture, or null if the browser can't. */
const shrinkForCard = (url, size = 512) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    const timer = setTimeout(() => resolve(null), 20000);
    img.onload = () => {
      clearTimeout(timer);
      try {
        const c = document.createElement("canvas");
        c.width = size;
        c.height = size;
        const ctx = c.getContext("2d");
        const iw = img.naturalWidth;
        const ih = img.naturalHeight;
        const s = Math.max(size / iw, size / ih);
        ctx.drawImage(img, (size - iw * s) / 2, (size - ih * s) / 2, iw * s, ih * s);
        c.toBlob(
          (blob) => {
            if (blob && blob.type === "image/webp") return resolve(blob);
            c.toBlob((jpg) => resolve(jpg || null), "image/jpeg", 0.88);
          },
          "image/webp",
          0.86
        );
      } catch {
        resolve(null); // tainted canvas: CORS
      }
    };
    img.onerror = () => {
      clearTimeout(timer);
      resolve(null);
    };
    img.src = url;
  });

/**
 * Puts the draft on the topic card. Resolves { imageUrl, small } — small is
 * false when the original (not a shrunk copy) had to be used.
 */
export const approveTopicPicture = async (topic) => {
  const id = topic.id || topic.slug;
  const draft = topic.imageDraft;
  if (!draft?.url) throw new Error("There's no new picture to approve.");
  let imageUrl = draft.url;
  let imagePath = draft.path || "";
  let small = false;
  const blob = await shrinkForCard(draft.url);
  if (blob) {
    const ext = blob.type === "image/webp" ? "webp" : "jpg";
    const stamp = new Date().toISOString().replace(/[-:]/g, "").replace(/\..*$/, "").replace("T", "-");
    const path = `topic-images/cards/${fileKeyOf(legacyKey(id) || exactKey(id))}-${stamp}.${ext}`;
    const r = storageRef(storage, path);
    await uploadBytes(r, blob, { contentType: blob.type, cacheControl: "public, max-age=31536000" });
    imageUrl = await getDownloadURL(r);
    imagePath = path;
    small = true;
  }
  const patch = {
    imageUrl,
    imagePath,
    imageSource: draft.path || draft.url,
    imagePrompt: draft.prompt || "",
    imageDraft: null,
  };
  await setDoc(libDoc(id), { ...patch, updatedAt: serverTimestamp() }, { merge: true });
  announce(id, patch);
  return { imageUrl, small };
};

/** Throws the new picture away; the card keeps what it had. */
export const discardTopicPicture = async (topic) => {
  const id = topic.id || topic.slug;
  await setDoc(libDoc(id), { imageDraft: null, updatedAt: serverTimestamp() }, { merge: true });
  announce(id, { imageDraft: null });
};

/** Takes the picture off the card (back to the ⭐). */
export const removeTopicPicture = async (topic) => {
  const id = topic.id || topic.slug;
  const patch = { imageUrl: "", imagePath: "" };
  await setDoc(libDoc(id), { ...patch, updatedAt: serverTimestamp() }, { merge: true });
  announce(id, patch);
};

/** Saves the typed idea on its own (so it survives a reload). */
export const saveTopicIdea = async (topic, idea) => {
  const id = topic.id || topic.slug;
  const patch = { imageIdea: String(idea || "").trim() };
  await setDoc(libDoc(id), patch, { merge: true });
  announce(id, patch);
};

/* ------------------------------------------------------------------ */
/* Pages that carry their own picture                                  */
/* ------------------------------------------------------------------ */

/**
 * Every page topic with its OWN imageUrl, which hides the library picture.
 * Reads all portalLayouts once (admin button only).
 * Resolves [{ slug, pages: [layoutId], urls: Set }] sorted by page count.
 */
export const findPagePictures = async () => {
  const snap = await getDocs(collection(db, "portalLayouts"));
  const bySlug = {};
  snap.forEach((d) => {
    (d.data().topics || []).forEach((t) => {
      const own = String(t?.imageUrl || "").trim();
      const slug = String(t?.slug || "").toLowerCase();
      if (!own || !slug) return;
      if (!bySlug[slug]) bySlug[slug] = { slug, pages: [], urls: new Set() };
      bySlug[slug].pages.push(d.id);
      bySlug[slug].urls.add(own);
    });
  });
  return Object.values(bySlug).sort((a, b) => b.pages.length - a.pages.length);
};

/**
 * Blanks the page's own picture for these slugs, so the library picture
 * shows. Each page is backed up to layoutBackups before it is written.
 * Resolves { pages, topics }.
 */
export const applyLibraryPictures = async (slugs, onProgress) => {
  const wanted = new Set((slugs || []).map((s) => String(s).toLowerCase()));
  const snap = await getDocs(collection(db, "portalLayouts"));
  const targets = snap.docs.filter((d) =>
    (d.data().topics || []).some((t) => wanted.has(String(t?.slug || "").toLowerCase()) && String(t?.imageUrl || "").trim())
  );
  const stamp = new Date().toISOString().replace(/[:.]/g, "-");
  let topics = 0;
  for (let k = 0; k < targets.length; k += 1) {
    const d = targets[k];
    const fresh = await getDoc(d.ref);
    if (!fresh.exists()) continue;
    const data = fresh.data();
    let changed = 0;
    const next = (data.topics || []).map((t) => {
      if (wanted.has(String(t?.slug || "").toLowerCase()) && String(t?.imageUrl || "").trim()) {
        changed += 1;
        return { ...t, imageUrl: "" };
      }
      return t;
    });
    if (changed) {
      await setDoc(doc(db, "layoutBackups", `${d.id}__${stamp}`), data);
      await updateDoc(d.ref, { topics: next });
      topics += changed;
    }
    if (onProgress) onProgress(k + 1, targets.length, d.id);
  }
  return { pages: targets.length, topics };
};

/** The library topic record for a slug (fresh from Firestore). */
export const getTopicFresh = async (topicId) => {
  const s = await getDoc(libDoc(topicId));
  return s.exists() ? { id: s.id, ...s.data() } : null;
};
